import React from 'react';
import {ExclamationCircleFilled} from '@ant-design/icons';
import {Modal, Space, Tag, Tooltip} from 'antd';
import {Link} from 'react-router-dom';
import {
  cardPaymentStatusesConstants,
  invoiceTypeConstants,
  expenseTypesConstants,
  transactionTypesConstants,
} from '../../constants';
import {
  FileInvoiceEuroIcon,
  ReceiptDeclinedIcon,
  ReceiptIcon,
  WireIcon,
  WithLessCreditCardIcon,
  WithLessOutsideIcon,
} from '../../icons';
import NameWithLink from '../../components/NameWithLink';
import CreditCardDetails from '../../components/pages/CardsPage/CreditCardDetails';
import {amountHelpers} from './amount.helpers';
import {cardsHelpers} from './cards.helpers';
import {colorHelpers} from './color.helpers';
import {dateHelpers} from './date.helpers';
import {expensesHelpers} from './expenses.helpers';
import {objectHelpers} from './object.helpers';
import {tableHelpers} from './table.helpers';
import {textHelpers} from './text.helpers';
import routes from '../../routes/routes.json';
import {TRANSACTION_PAGE_SEARCH_KEYS} from '../../pages/TransactionsPage/transactionPage.constants';

const {CARD_TRANSACTION} = transactionTypesConstants;

const {AUTHORIZATION_ACCEPTED, CLEARED, DECLINED, REFUND, REVERSED, SETTLED} = cardPaymentStatusesConstants;

const {SELECTED_TRANSACTION_ID, SELECTED_TRANSACTION_DATE} = TRANSACTION_PAGE_SEARCH_KEYS;

const gMV = amountHelpers.getAmountWithCurrencyCode;

const cardPaymentStatuses = {
  [AUTHORIZATION_ACCEPTED]: {
    key: 'cardPaymentStatuses.authorized',
  },
  [CLEARED]: {
    key: 'cardPaymentStatuses.authorized',
  },
  [DECLINED]: {
    key: 'cardPaymentStatuses.declined',
    color: '#FF7875'
  },
  [REFUND]: {
    key: 'cardPaymentStatuses.refund',
  },
  [REVERSED]: {
    key: 'cardPaymentStatuses.reversed',
  },
  [SETTLED]: {
    key: 'cardPaymentStatuses.settled',
  },
};

export const transactionsHelpers = {
  additionalTransactionsGrouping: (transactions, summarizeKeyName) => transactions.map((t, key) => (
    (key !== 0 && key !== transactions.length - 1 && transactions[key + 1][summarizeKeyName])
      ? {...t, isLatest: true} : t)
  ),
  getAnimationStyles: ({
    defaultTableWidth = '100%',
    duration,
    detailsMaxWidth = 472
  } = {}) => {
    const transactionDetailsMaxWidth = `${detailsMaxWidth + 24}px`;
    const tableCollapseWidth = `calc(100% - ${transactionDetailsMaxWidth})`;
    const transactionDetailsHiddenRightPosition = `calc(-${transactionDetailsMaxWidth} - 10px)`;

    return {
      details: {
        default: {
          top: 0,
          position: 'absolute',
          transition: `all ${duration}ms ease-in-out`,
          width: transactionDetailsMaxWidth
        },
        entering: {opacity: 50, right: 0},
        entered: {opacity: 1, right: 0},
        exiting: {opacity: 50, right: transactionDetailsHiddenRightPosition},
        exited: {opacity: 0, right: transactionDetailsHiddenRightPosition}
      },
      table: {
        default: {
          transition: `width ${duration}ms ease-in-out`
        },
        entering: {width: tableCollapseWidth},
        entered: {width: tableCollapseWidth},
        exiting: {width: defaultTableWidth},
        exited: {width: defaultTableWidth}
      }
    }
  },
  isReturnedTransaction: (transaction) => transaction && [DECLINED, REVERSED].includes(transaction.payment_status),
  getTransactionAmount: ({transaction, variant, groupingKey = 'isMonthGrouping'}) => {
    if (transaction && typeof transaction === 'object') {
      let className = 'amount';
      let {amount} = transaction;
      if (transaction[groupingKey]) {
        className = 'total-amount';
        amount = transaction[variant] || 0;
        if (amount > 0 && variant === 'outcome') className = `${className} danger-text`;
        if (variant === 'outcome' && Math.sign(amount) > 0) amount = amount * -1;
        return <span className={className}>{gMV(amount)}</span>;
      } else {
        const isIncoming = transaction.is_incoming || false;
        const isPositiveAmount = Boolean(parseFloat(amount));
        if ((isIncoming && variant !== 'income') || (!isIncoming && variant !== 'outcome')) return '';
        const price = gMV(amount);
        const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
        if (!isIncoming && !isReturnedTransaction) className = `${className} danger-text`;
        if (isReturnedTransaction) className = `${className} returned-price`;
        return amount ? isIncoming ? price : <span className={className}>{isPositiveAmount && '-'}{gMV(Math.abs(amount))}</span> : null;
      }
    } else {
      return null
    }
  },

  isTransactionWithExpense: (transaction) => {
    const transactionType = transaction?.transaction_type;
    return transactionType === transactionTypesConstants.CARD_TRANSACTION
      || transactionType === transactionTypesConstants.PAYOUT;
  },

  getTransactionAuthor: (transaction) =>
    transaction?.user && transactionsHelpers.isTransactionWithExpense(transaction)
      ? tableHelpers.getUserAvatar({user: transaction.user}) : null,

  getTransactionCardNumberWithLink: ({transaction, cardSymbolsLength = 5, nameSymbolsLength} = {}) => {
    if (!transaction) return null;

    const { card_id: cardId, user_id: userId } = transaction;
    const value = transactionsHelpers.getTransactionCardNumberWithName({
      transaction, cardSymbolsLength, nameSymbolsLength
    });
    const className = `d-inline-flex row-link-container${cardId && userId ? ' active-link' : ''}`;

    if (cardId && userId) {
      const url = cardsHelpers.getCardDetailsUrl({ cardId, userId });
      return (
        <div className={className}>
          <Link onClick={(e) => e.stopPropagation()} to={url}>
            <NameWithLink name={value} url={url} variant='text' />
          </Link>
        </div>
      );
    }

    return <div className={className}>{value}</div>;
  },

  getTransactionCardNumberWithName: ({transaction, cardSymbolsLength = 5, nameSymbolsLength}) => {
    let name = transaction?.card_name || '';
    const number = cardsHelpers.hideCardNumber(transaction?.masked_pan, cardSymbolsLength) || '';
    if (nameSymbolsLength) name = textHelpers.cutString(name, nameSymbolsLength);
    return `${number} - ${name}`;
  },

  getTransactionDate: (transaction, format = 'LLL') => {
    const authorizationDate = transaction?.authorization_date;
    const isDeclined = transactionsHelpers.isDeclinedTransaction(transaction);

    // for declined transactions show authorization_issuer_time if exists
    if (isDeclined) {
      const authorizationIssuerTime = transaction?.authorization_issuer_time;
      if (authorizationIssuerTime) return dateHelpers.getDateFromISO(authorizationIssuerTime, format);
    }

    const createdDate = transaction?.created_date;
    const dateValue = authorizationDate || createdDate || null;
    return dateValue ? dateHelpers.getDateFromISO(dateValue, format) : '';
  },

  getTransactionDateWithGrouping: (transaction, groupedPropName) => {
    const date = transaction?.created_date;
    const isGrouped = transaction?.[groupedPropName];
    if (!date) return '';
    return isGrouped
      ? dateHelpers.getDateWithMonth(date, 'MMM YYYY')
      : transactionsHelpers.getTransactionDate(transaction, 'DD MMM');
  },

  getTransactionSource: (transaction) => {
    let value = '';
    if (typeof transaction === 'object') {
      const description = objectHelpers.getObjProp(transaction, 'description');
      const source = objectHelpers.getObjProp(transaction, 'source');
      if (source) {
        value = source;
      } else if (description && description !== '') {
        value = textHelpers.cutString(description.split(' ')[0], 20)
      }
    }
    return value;
  },
  getTransactionVendor: (transaction) => {
    if (!transaction) return null;
    return  {
      name: transaction?.expense?.vendor_local_name  || transaction?.expense?.vendor,
      vendor_id: transaction?.expense?.vendor_local_id
    }
  },
  getTransactionSourceWithIcon: ({
    categories = [],
    enabledTooltip = true,
    transaction,
  }) => {
    if (transaction?.isMonthGrouping || transaction?.isYearlyGrouping) return ;

    let value = transactionsHelpers.getTransactionSource(transaction);
    const mccCode = transaction?.mcc_code;
    const category = (
      categories.find(c => c.mcc_code === mccCode) ||
      categories.find(c => objectHelpers.isNaV(c.mcc_code))
    )?.category;

    if (category) {
      const categoryIcon = expensesHelpers.getExpenseCategoryIcon(category?.id);
      value = (
        <Space align='center' className='d-flex'>
          <div className='d-flex'>
            {enabledTooltip ? (
              <Tooltip
                title={category?.name || ''}
                trigger='hover'
              >
                <div className='d-flex'>{categoryIcon}</div>
              </Tooltip>
            ): categoryIcon}
          </div>
          <div>{value}</div>
        </Space>
      );
    }
    return value;
  },
  getTransactionStatus: (status) => {
    const statuses = Object.fromEntries(
      Object.entries(cardPaymentStatuses).map(([key, value]) => [
        key,
        { ...value, bgColor: value?.color ? colorHelpers.hex2rgba(value.color, 0.20) : '#F3F3F4' },
      ])
    );

    return objectHelpers.getStatus(status, statuses);
  },
  getTransactionType: (transaction) => {
    if (transaction && typeof transaction === 'object') {
      const transactionType = transaction.transaction_type;
      let element = <WireIcon />;
      if (transactionType === CARD_TRANSACTION) {
        element = (
          <CreditCardDetails
            cardNumber={transaction.masked_pan}
            cardNumberClassName='card-number'
          />
        );
      }
      return <span className='transaction-type'>{element}</span>;
    } else {
      return null;
    }
  },
  getTransactionTypeIcon: (transaction, isOutside = false) => {
    if (isOutside) return <WithLessOutsideIcon />;
    if (!transaction) return null;
    return transaction.transaction_type === CARD_TRANSACTION ? <WithLessCreditCardIcon /> : <WireIcon />
  },
  getTransactionPaymentMethodDetails: (transaction) => {
    if (!transaction) return null;

    const isCardTransaction = transaction.transaction_type === CARD_TRANSACTION;
    const icon = transactionsHelpers.getTransactionTypeIcon(transaction);
    const value = isCardTransaction ? transactionsHelpers.getTransactionCardNumberWithLink({transaction}) : '';
    return (
      <Space align='center' size={4} className='transaction-type d-flex-child'>
        {icon}<span>{value}</span>
      </Space>
    );
  },
  getTransactionAttachmentIcon: ({transaction, translation}) => {
    if (!transaction?.expense) return null;
    return expensesHelpers.getExpenseReviewInfo({expense: transaction.expense, translation});
  },
  getWireDetails: (details) => {
    const gObjProp = (propName) => objectHelpers.getObjProp(details, propName);
    const getFormattedValue = (value) => value ? value.match(/.{1,4}/g).join(' ') : '';

    return {
      bic: getFormattedValue(gObjProp('bic')),
      iban: getFormattedValue(gObjProp('iban'))
    };
  },
  getInvoiceEmail: (settings) => {
    let email = null;
    const gObjProp = (propName) => objectHelpers.getObjProp(settings, propName);

    if (settings) email = gObjProp('invoice_email_alias') || gObjProp('invoice_email') || null;
    return email;
  },
  addFixedDetailsWindowScrollEventListener: ({detailsWindowFixedHeight, open, isFixedDetailsWindow, setIsFixedDetailsWindow}) => {
    const setScroll = (e) => {
      let scrolled = false;
      if (!open) return;
      if (e.target.scrollTop >= detailsWindowFixedHeight) scrolled = true;
      if (isFixedDetailsWindow !== scrolled) setIsFixedDetailsWindow(scrolled);
    };
    document.querySelector('#app-page-container')?.addEventListener('scroll', setScroll);
    return () => {
      document.querySelector('#app-page-container')?.removeEventListener('scroll', setScroll);
    };
  },
  getGroupedTransactions: (transactions, totals = []) => {
    const daysList = {};
    const datePropName = 'created_date';
    const summarizeKeyName = 'isMonthGrouping';
    let groupedTransactions = [];
    let lastAdded;

    // define transactions months
    transactions.forEach(transaction => {
      const date = transaction[datePropName].slice(0, 7);
      const {is_incoming: isIncoming} = transaction;
      const amount = Number(transaction.amount) || 0;
      const income = isIncoming ? amount : 0;
      const outcome = isIncoming ? 0 : amount;
      const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
      if (!isReturnedTransaction) {
        if (daysList.hasOwnProperty(date)) {
          daysList[date] = {
            income: daysList[date].income + income,
            outcome: daysList[date].outcome + outcome
          }
        } else {
          daysList[date] = {
            income,
            outcome
          }
        }
      }
    });

    // add grouping rows to transactions list
    transactions.forEach(t => {
      const date = t[datePropName].slice(0, 7);
      if (lastAdded !== date && daysList.hasOwnProperty(date)) {
        let groupedTransaction = {
          id: date,
          [summarizeKeyName]: true,
          [datePropName]: date,
          ...daysList[date]
        }
        const totalByDate = totals.find(t => `${t.year}-${t.month}` === date);
        if (totalByDate) {
          groupedTransaction = {
            ...groupedTransaction,
            income: totalByDate?.total_in,
            outcome: totalByDate?.total_out
          }
        }
        lastAdded = date;
        groupedTransactions.push(groupedTransaction);
      }
      groupedTransactions.push(t);
    });
    groupedTransactions = transactionsHelpers.additionalTransactionsGrouping(groupedTransactions, summarizeKeyName);
    return groupedTransactions;
  },
  getInvoiceTypeOptions: ({t, enabledEmpty = false}) => {
    let options = [
      {
        icon: <FileInvoiceEuroIcon />,
        label: t(`invoiceTypes.invoice`),
        value: invoiceTypeConstants.INVOICE
      },
      {
        icon: <ReceiptIcon />,
        label: t(`invoiceTypes.receipt`),
        value: invoiceTypeConstants.RECEIPT
      }
    ];
    if (enabledEmpty) {
      options = [
        ...options,
        {
          icon: <ReceiptDeclinedIcon />,
          label: t(`invoiceTypes.noReceipt`),
          value: invoiceTypeConstants.NO_RECEIPT
        }
      ];
    }
    return options;
  },
  getExpenseTypeOptions: ({t}) => {
    return [
      {
        label: t(`expenseTypes.invoice`),
        value: expenseTypesConstants.INVOICE
      },
      {
        label: t(`expenseTypes.receipt`),
        value: expenseTypesConstants.RECEIPT
      },
    ];
  },
  isMonthlyGroupedRow: (row) => row.isMonthGrouping,
  getTransactionsWithExpense: ({employees, expenses, transactions}) => {
    return transactions.map(t => {
      const {expense_id: expenseId, user_id: userId} = t;
      const user = employees.find(e => e.employee_id === userId);
      const expense = expenses.find(e => e.id === expenseId);
      return {
        ...t,
        expense,
        user
      }
    });
  },
  getRowClassName: ({
    groupedPropName,
    index,
    record,
    selectedRowIndex
  }) => {
    let className = '';
    if (record?.[groupedPropName]) className += ' grouped-row';
    if (index !== undefined && index === selectedRowIndex) className += ' selected-row';
    return className;
  },
  getUpdatedBatchExpensesList: (expenses, updatedExpense) =>
    expenses.map(expense => expense.id === updatedExpense.id ? {...expense, ...updatedExpense} : expense),
  removeInvoiceFromTransaction: ({
    t,
    transaction,
    initialCallback,
    successCallback,
    errorCallback,
    unlinkExpense,
    onExpenseUpdate
  }) => {
    if (transaction.expense) {
      Modal.confirm({
        title: t('transactions:modal.removeInvoice.title'),
        icon: <ExclamationCircleFilled />,
        content: t('transactions:modal.removeInvoice.description'),
        okText: t('main:yes'),
        okType: 'danger',
        cancelText: t('main:no'),
        cancelButtonProps: {size: 'large'},
        okButtonProps: {size: 'large'},
        onCancel() {
          errorCallback && errorCallback();
        },
        onOk() {
          const {expense} = transaction;
          initialCallback?.();
          unlinkExpense({
            data: {expense_id: expense.id},
            successCallback: () => {
              onExpenseUpdate({
                ...expense,
                expense_type: null,
                expense_invoice_status: undefined,
              });
              successCallback?.();
            },
            errorCallback: (resp) => errorCallback && errorCallback(resp)
          })
        }
      });
    } else {
      errorCallback && errorCallback();
    }
  },
  getTransactionInvoiceType: (transaction) => {
    let expense = transaction?.expense;
    if (!expense) return undefined;

    return expense?.subscription_id ? invoiceTypeConstants.INVOICE : invoiceTypeConstants.RECEIPT;
  },
  getDeclinedTransactionStatuses: () => [cardPaymentStatusesConstants.DECLINED, cardPaymentStatusesConstants.REVERSED, cardPaymentStatusesConstants.REFUND],
  getTransactionDetailsRightContent: ({transaction, t}) => {
    const paymentStatus = transaction?.payment_status;
    if (!paymentStatus) return null;

    const status = transactionsHelpers.getTransactionStatus(paymentStatus);

    const tagStyle = {
      backgroundColor: status.bgColor || 'unset',
      border: 'none',
      borderRadius: 16,
      color: status.color,
      lineHeight: '24px',
      margin: 0
    };

    let tagName = t(status.key);
    const authorizationNote = transaction?.authorization_note;
    const isDeclined = paymentStatus === cardPaymentStatusesConstants.DECLINED;

    if (isDeclined && authorizationNote) tagName = `${tagName}: ${authorizationNote}`;

    const tagElement = (
      <Tag style={tagStyle}>
        {textHelpers.cutString(tagName, 22)}
      </Tag>
    );

    return (
      <div className='truncate' style={{ marginLeft: 'auto', textAlign: 'right', fontSize: 12, maxWidth: 200 }}>
        {isDeclined ? <Tooltip title={tagName}>{tagElement}</Tooltip> : tagElement}
      </div>
    );
  },
  isDeclinedTransaction: (transaction) =>
    transaction && transactionsHelpers.getDeclinedTransactionStatuses().includes(transaction.payment_status),
  getMissingInfoFilterParams: (employeeId) => {
    const params = {
      payment_status_not_in: transactionsHelpers.getDeclinedTransactionStatuses(),
      has_invoice: false,
      amount_gte: 0.01,
    }
    if (employeeId) {
      params.user_id_in = employeeId;
    }
    return params;
  },
  getTransactionDetailsUrl: (transaction) => {
    if (!transaction) return null;

    const id = transaction.expense_id;
    if (!id) return null;

    const date = dateHelpers.getDateWithMonth(transaction.created_date, 'YYYY-MM-DD')
    return `${routes.transactionsList}?${SELECTED_TRANSACTION_ID}=${id}&${SELECTED_TRANSACTION_DATE}=${date}`;
  }
}
